import { isPreRelease } from '@/utils/environment';

/**
 * Dashboard engagement events (session starts, page views, card clicks),
 * queued in memory and posted in small batches to /engagement/events/.
 * Sign-ins and password resets are recorded by the API itself.
 *
 * Fire and forget: a failed post is dropped, never retried, so tracking can't
 * slow a page or pile up requests. Page names are route patterns
 * ("/customers/:customerId/locations"), never raw URLs, so ids and reset
 * tokens stay out of the table.
 */
export type EngagementEventType =
  'session_start' | 'page_view' | 'card_click' | 'card_view';

type EngagementEvent = {
  type: EngagementEventType;
  name: string;
  customer_id: string | null;
};

type Sender = (events: EngagementEvent[]) => Promise<unknown>;

const MAX_BATCH = 50;
const FLUSH_DELAY_MS = 5_000;
// Double clicks and click-throughs count as one click on a card.
const SAME_CLICK_MS = 1_000;

let send: Sender | null = null;
let queue: EngagementEvent[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let routeCustomerId: string | null = null;
let defaultCustomerId: string | null = null;
let lastClick = { name: '', at: 0 };
// Cards already recorded as seen on this page visit.
const seenCards = new Set<string>();

export const flushEngagement = () => {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!send || queue.length === 0) return;
  while (queue.length) {
    send(queue.splice(0, MAX_BATCH)).catch(() => {});
  }
};

/** Null stops tracking (signed out, or a host the API doesn't support yet). */
export const setEngagementSender = (sender: Sender | null) => {
  send = sender;
  if (!sender) queue = [];
};

/** The customer a page is about: the route's customerId, else the signed-in
 *  customer user's own customer. */
export const setEngagementCustomer = (
  route: string | null,
  fallback: string | null
) => {
  routeCustomerId = route;
  defaultCustomerId = fallback;
};

export const track = (type: EngagementEventType, name = '') => {
  if (!send) return;
  if (type === 'page_view') seenCards.clear();
  if (type === 'card_view') {
    if (seenCards.has(name)) return;
    seenCards.add(name);
  }
  if (type === 'card_click') {
    const now = Date.now();
    if (name === lastClick.name && now - lastClick.at < SAME_CLICK_MS) return;
    lastClick = { name, at: now };
  }
  queue.push({
    type,
    name: name.slice(0, 200),
    customer_id: routeCustomerId ?? defaultCustomerId,
  });
  if (queue.length >= MAX_BATCH) flushEngagement();
  else if (!timer) timer = setTimeout(flushEngagement, FLUSH_DELAY_MS);
};

export type Audience = 'customer' | 'internal';

export type TrackedCard = {
  name: string;
  /** Who sees it: the customer portal or the internal dashboard. */
  audience: Audience;
  /** False while it's still dev-only (PRE_RELEASE), so it can't be judged on prod use yet. */
  onProd: boolean;
};

const card = (name: string, audience: Audience): TrackedCard => ({
  name,
  audience,
  onProd: !isPreRelease(name),
});

/**
 * Every card that carries a data-track name, for the Engagement page's
 * keep-or-drop rankings: the API only knows cards somebody used, and each card
 * is judged on its own audience. onProd follows PRE_RELEASE
 * (src/utils/environment.ts). trackedCards.test.ts fails CI if a data-track
 * name is missing here or a name here is no longer used.
 */
export const TRACKED_CARDS: TrackedCard[] = [
  // Internal dashboard (src/pages/dashboard)
  card('Battery Compliance', 'internal'),
  card('Cart Health', 'internal'),
  card('Cart Status', 'internal'),
  card('Cart Usage', 'internal'),
  card('Carts', 'internal'),
  card('Issues History', 'internal'),
  card('Usage History', 'internal'),
  card('Issues Map', 'internal'),
  card('Pipeline Performance', 'internal'),
  // Customer portal (src/pages/customer-dashboard)
  card('Action Items', 'customer'),
  card('Busiest Hours', 'customer'),
  card('Charging Status', 'customer'),
  card('Fleet Health', 'customer'),
  card('Fleet Map', 'customer'),
  card('Fleet Overview', 'customer'),
  card('Store Comparison', 'customer'),
  card('This Week', 'customer'),
  card('Usage Stats', 'customer'),
];

/** Which app an event came from; the API sets it from the request Origin. */
export type EngagementApp = 'dashboard' | 'companion';

/**
 * The companion app's screens and actions (zeos-companion-app
 * src/lib/engagement.ts). Keep in sync there; the Engagement page warns
 * about any COMPANION_PREFIX name it doesn't find here. Screens are page
 * views, actions are card clicks; all are customer-facing.
 */
export const COMPANION_PREFIX = 'Companion: ';
export const COMPANION_SCREENS = [
  'Companion: Home',
  'Companion: Store switcher',
  'Companion: Cart detail',
];
export const COMPANION_ACTIONS = [
  'Companion: Opened a cart',
  'Companion: Switched store',
  'Companion: Pulled to refresh',
];

/** The customer portal's tabs, recorded as page views by customerPortalShell. */
export const PORTAL_TAB_PAGES = [
  'dashboard',
  'live-view',
  'trends',
  'my-fleet',
  'cart detail',
].map((tab) => `/home tab: ${tab}`);

const INTERACTIVE =
  'a, button, input, select, textarea, summary, [role="button"], [role="tab"], [role="link"], [role="combobox"], [role="option"], [role="checkbox"], [role="switch"], [data-track-click]';

/**
 * Which card a click landed on, or null. Only clicks on something interactive
 * count (a link, button, tab, select...), so reading a card and clicking
 * empty space isn't engagement. Only cards with an explicit data-track name
 * count: guessing from a heading picked up admin pages' own cards and even
 * people's names. The nearest data-track wins, so map popups report the map.
 */
export const cardForClick = (target: EventTarget | null): string | null => {
  if (!(target instanceof Element)) return null;
  const control = target.closest(INTERACTIVE);
  if (!control) return null;
  return control.closest('[data-track]')?.getAttribute('data-track') || null;
};

// A card counts as seen once at least half of it has been on screen for this
// long, so scrolling past it doesn't count.
const SEEN_MS = 2_000;

/**
 * Records card_view for every data-track card that is actually looked at:
 * half on screen for 2 s while the tab is visible, once per page visit (the
 * seen set resets on each page view). Cards that mount later (lazy pages,
 * tab switches) are picked up by the MutationObserver. Returns a stop function.
 */
export const watchCardViews = () => {
  const timers = new Map<Element, ReturnType<typeof setTimeout>>();
  const watched = new WeakSet<Element>();

  const intersection = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        const el = entry.target;
        if (entry.isIntersecting && document.visibilityState === 'visible') {
          if (timers.has(el)) continue;
          timers.set(
            el,
            setTimeout(() => {
              timers.delete(el);
              const name = el.getAttribute('data-track');
              if (name && el.isConnected) track('card_view', name);
            }, SEEN_MS)
          );
        } else {
          clearTimeout(timers.get(el));
          timers.delete(el);
        }
      }
    },
    { threshold: 0.5 }
  );

  const scan = () => {
    document.querySelectorAll('[data-track]').forEach((el) => {
      if (watched.has(el)) return;
      watched.add(el);
      intersection.observe(el);
    });
  };
  scan();
  // Debounced: the map and charts change the DOM constantly.
  let pending: ReturnType<typeof setTimeout> | null = null;
  const mutations = new MutationObserver(() => {
    if (pending) return;
    pending = setTimeout(() => {
      pending = null;
      scan();
    }, 500);
  });
  mutations.observe(document.body, { childList: true, subtree: true });

  return () => {
    mutations.disconnect();
    if (pending) clearTimeout(pending);
    intersection.disconnect();
    timers.forEach(clearTimeout);
    timers.clear();
  };
};
